import { setupI18n } from '@lingui/core';
import { displayLocale } from '@/utils/displayLocale';
import { messages as enMessages } from './en/messages.po?lingui';

export const i18n = setupI18n();

export const supportedLocales = ['en', 'fr', 'es', 'ru', 'zh', 'ja', 'ko'] as const;
export type SupportedLocale = (typeof supportedLocales)[number];

const isSupportedLocale = (locale: string): locale is SupportedLocale => supportedLocales.includes(locale as SupportedLocale);

/**
 * Endonyms — a language picker has to be readable by someone who can't read the current UI
 * language, so these are never translated.
 */
export const localeLabels: Record<SupportedLocale, string> = {
  en: 'English',
  fr: 'Français',
  es: 'Español',
  ru: 'Русский',
  zh: '中文',
  ja: '日本語',
  ko: '한국어',
};

// One entry per non-English locale so Vite keeps each catalog in its own lazy chunk —
// a template-literal dynamic import would bundle every catalog into one shared chunk.
const catalogLoaders: Record<Exclude<SupportedLocale, 'en'>, () => Promise<{ messages: typeof enMessages }>> = {
  fr: () => import('./fr/messages.po?lingui'),
  es: () => import('./es/messages.po?lingui'),
  ru: () => import('./ru/messages.po?lingui'),
  zh: () => import('./zh/messages.po?lingui'),
  ja: () => import('./ja/messages.po?lingui'),
  ko: () => import('./ko/messages.po?lingui'),
};

/** Narrows whatever `i18n.locale` currently holds (or a stale localStorage value) to a known locale. */
export const toSupportedLocale = (locale: string | null | undefined): SupportedLocale =>
  locale && isSupportedLocale(locale) ? locale : 'en';

export async function activateLocale(requestedLocale: string | null): Promise<SupportedLocale> {
  let locale = toSupportedLocale(requestedLocale);
  // English is the source catalog and always needed as the fallback. Every other language
  // lives in its own lazy chunk so English users never download any of them.
  let messages = enMessages;
  if (locale !== 'en') {
    try {
      messages = (await catalogLoaders[locale]()).messages;
    } catch (error) {
      // A failed lazy chunk (stale HTML after a deploy, flaky network) must degrade this
      // session to English — main.tsx top-level-awaits this call, so a rejection here is a
      // permanently blank page for everyone with this locale persisted.
      console.error(`Failed to load the "${locale}" catalog, falling back to English`, error);
      locale = 'en';
    }
  }
  i18n.load(locale, messages);
  // The second argument is Lingui's Intl formatting locale: i18n.number()/i18n.date() and
  // ICU-embedded formats follow the browser, while message texts follow the picked language.
  i18n.activate(locale, [displayLocale]);
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
  }
  return locale;
}
